/**
 * Manages contextual training-alerts nudge popups (anonymous + authenticated).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOverlayAdCoordinator } from '../../ads/OverlayAdCoordinator';
import { acquireOverlayWhenAvailable } from '../../ads/acquireOverlayWhenAvailable';
import {
  TRAINING_ALERTS_NUDGE_EVENT,
  isTrainingAlertsNudgeDetail,
  type TrainingAlertsNudgeDetail,
} from '../../../shared/conversion/trainingAlertsNudgeEvents';
import {
  bumpSessionVisit,
  canShowNudge,
  readTrainingAlertsNudgeState,
  recordHardOptOut,
  recordShown,
  recordSoftDismiss,
  recordSubscribed,
  wasNudgeShownThisSession,
} from '../../../shared/conversion/trainingAlertsNudgeFrequency';
import { isTrainingAlertsNudgePathBlocked } from '../../../shared/conversion/trainingAlertsNudgePaths';
import TrainingEventGeoAlertsModal from '../events/TrainingEventGeoAlertsModal';
import TrainingAlertsNudgeDialog from './TrainingAlertsNudgeDialog';
import { getMyTrainingGeoAlerts } from '../../services/trainingEventGeoAlertsApi';
import { trackEvent } from '../../utils/analytics';
import { isViteFlagEnabled } from '../../utils/runtimePlatformFlags';

const OVERLAY_CLAIM_ID = 'training-alerts-nudge';
const AUTH_PROBE_SESSION_KEY = 'apt_training_alerts_nudge_auth_probed';

function clientFlagEnabled(): boolean {
  return isViteFlagEnabled(
    'VITE_TRAINING_ALERTS_NUDGE_ENABLED',
    import.meta.env.VITE_TRAINING_ALERTS_NUDGE_ENABLED,
  );
}

function browserLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function browserSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

const TrainingAlertsNudgeManager: React.FC = () => {
  const enabled = clientFlagEnabled();
  const navigate = useNavigate();
  const { isAuthenticated, isInitializing } = useAuth();
  const { release } = useOverlayAdCoordinator();

  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [geoModalOpen, setGeoModalOpen] = useState(false);
  const [pendingDetail, setPendingDetail] =
    useState<TrainingAlertsNudgeDetail | null>(null);

  const showingRef = useRef(false);
  const abortRef = useRef({ aborted: false });
  const pendingRef = useRef<TrainingAlertsNudgeDetail | null>(null);
  const geoModalSavedRef = useRef(false);

  useEffect(() => {
    pendingRef.current = pendingDetail;
  }, [pendingDetail]);

  const releaseOverlay = useCallback(() => {
    release('provider_acquisition', OVERLAY_CLAIM_ID);
  }, [release]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    bumpSessionVisit(browserLocalStorage(), browserSessionStorage());
  }, [enabled]);

  useEffect(() => {
    if (!enabled || isInitializing || !isAuthenticated) {
      return;
    }
    const session = browserSessionStorage();
    if (!session) {
      return;
    }
    try {
      if (session.getItem(AUTH_PROBE_SESSION_KEY) === '1') {
        return;
      }
      session.setItem(AUTH_PROBE_SESSION_KEY, '1');
    } catch {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const res = await getMyTrainingGeoAlerts();
        if (cancelled) {
          return;
        }
        const hasAlerts =
          res.data.email_enabled === true ||
          (Array.isArray(res.data.states) && res.data.states.length > 0);
        if (hasAlerts) {
          recordSubscribed(browserLocalStorage());
        }
      } catch {
        // Guest or network — ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, isAuthenticated, isInitializing]);

  const tryShowNudge = useCallback(
    async (detail: TrainingAlertsNudgeDetail) => {
      if (!enabled || showingRef.current || nudgeOpen || geoModalOpen) {
        return;
      }
      if (isTrainingAlertsNudgePathBlocked(window.location.pathname)) {
        return;
      }
      const local = browserLocalStorage();
      const session = browserSessionStorage();
      const state = readTrainingAlertsNudgeState(local);
      if (
        !canShowNudge(state, Date.now(), {
          shownThisSession: wasNudgeShownThisSession(session),
        })
      ) {
        return;
      }

      abortRef.current.aborted = true;
      abortRef.current = { aborted: false };
      const signal = abortRef.current;

      const acquired = await acquireOverlayWhenAvailable(
        'provider_acquisition',
        OVERLAY_CLAIM_ID,
        { signal, timeoutMs: 45_000 },
      );
      if (!acquired || signal.aborted) {
        return;
      }
      // Re-check path after await — user may have navigated during overlay wait.
      if (isTrainingAlertsNudgePathBlocked(window.location.pathname)) {
        releaseOverlay();
        return;
      }
      if (showingRef.current || wasNudgeShownThisSession(session)) {
        releaseOverlay();
        return;
      }
      if (
        !canShowNudge(readTrainingAlertsNudgeState(local), Date.now(), {
          shownThisSession: wasNudgeShownThisSession(session),
        })
      ) {
        releaseOverlay();
        return;
      }

      showingRef.current = true;
      recordShown(local, session);
      setPendingDetail(detail);
      setNudgeOpen(true);
      trackEvent({
        action: 'shown',
        category: 'training_alerts_nudge',
        label: detail.trigger,
        custom_parameters: {
          accept_mode: detail.acceptMode,
          page_path: window.location.pathname,
          audience: isAuthenticated ? 'auth' : 'guest',
        },
      });
    },
    [enabled, nudgeOpen, geoModalOpen, isAuthenticated, releaseOverlay],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onNudge = (event: Event) => {
      const custom = event as CustomEvent;
      if (!isTrainingAlertsNudgeDetail(custom.detail)) {
        return;
      }
      void tryShowNudge(custom.detail);
    };

    window.addEventListener(TRAINING_ALERTS_NUDGE_EVENT, onNudge);
    return () => {
      window.removeEventListener(TRAINING_ALERTS_NUDGE_EVENT, onNudge);
    };
  }, [enabled, tryShowNudge]);

  useEffect(() => {
    return () => {
      abortRef.current.aborted = true;
      if (showingRef.current) {
        releaseOverlay();
      }
    };
  }, [releaseOverlay]);

  const finishNudgeUi = useCallback(() => {
    setNudgeOpen(false);
    showingRef.current = false;
    releaseOverlay();
  }, [releaseOverlay]);

  const handleSoftDismiss = useCallback(() => {
    recordSoftDismiss(
      browserLocalStorage(),
      Date.now(),
      browserSessionStorage(),
    );
    trackEvent({
      action: 'soft_dismiss',
      category: 'training_alerts_nudge',
      label: pendingRef.current?.trigger,
    });
    finishNudgeUi();
    setPendingDetail(null);
  }, [finishNudgeUi]);

  const handleHardOptOut = useCallback(() => {
    recordHardOptOut(browserLocalStorage());
    trackEvent({
      action: 'hard_dismiss',
      category: 'training_alerts_nudge',
      label: pendingRef.current?.trigger,
    });
    finishNudgeUi();
    setPendingDetail(null);
  }, [finishNudgeUi]);

  const handleClose = useCallback(() => {
    handleSoftDismiss();
  }, [handleSoftDismiss]);

  const handleAccept = useCallback(() => {
    const detail = pendingRef.current;
    trackEvent({
      action: 'accept',
      category: 'training_alerts_nudge',
      label: detail?.trigger,
      custom_parameters: {
        accept_mode: detail?.acceptMode ?? 'modal',
      },
    });
    finishNudgeUi();
    if (!detail || detail.acceptMode === 'alerts') {
      setPendingDetail(null);
      navigate('/alerts');
      return;
    }
    geoModalSavedRef.current = false;
    setGeoModalOpen(true);
  }, [finishNudgeUi, navigate]);

  const handleGeoModalClose = useCallback(() => {
    // Abandoned subscribe after accept — apply soft snooze so we do not
    // immediately re-offer next session without the balanced cooldown.
    if (!geoModalSavedRef.current) {
      recordSoftDismiss(
        browserLocalStorage(),
        Date.now(),
        browserSessionStorage(),
      );
      trackEvent({
        action: 'soft_dismiss',
        category: 'training_alerts_nudge',
        label: pendingRef.current?.trigger ?? 'geo_modal_abandon',
        custom_parameters: { reason: 'geo_modal_abandon' },
      });
    }
    setGeoModalOpen(false);
    setPendingDetail(null);
  }, []);

  const handleGeoModalSaved = useCallback(() => {
    // Mark success so close does not soft-snooze. Leave open/close to the modal
    // (auth closes itself; guest stays open for the confirmation success UI).
    geoModalSavedRef.current = true;
    recordSubscribed(browserLocalStorage());
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <>
      <TrainingAlertsNudgeDialog
        open={nudgeOpen}
        onAccept={handleAccept}
        onSoftDismiss={handleSoftDismiss}
        onHardOptOut={handleHardOptOut}
        onClose={handleClose}
      />
      <TrainingEventGeoAlertsModal
        open={geoModalOpen}
        onClose={handleGeoModalClose}
        onSaved={handleGeoModalSaved}
        initialGeoStateId={pendingDetail?.geoStateId ?? null}
        initialCountyIds={pendingDetail?.countyIds ?? []}
        guestSource={
          pendingDetail?.guestSource ??
          (pendingDetail?.trigger
            ? `nudge_${pendingDetail.trigger}`
            : 'nudge')
        }
      />
    </>
  );
};

export default TrainingAlertsNudgeManager;
